import axios, { AxiosProgressEvent } from "axios";
import { url, AuthHeader } from "../../utils/_exports";
import Emitter from "../../utils/emitter";
import { SharepointFolderPath } from "../../types/sharepointApi";
const apiURL = "sharepoint";

const getRootFolderId = (tenantKey: any, id: any, type: any) => {
	return new Promise((resolve, reject) => {
		axios
			.get(
				`${url}/${tenantKey}/${apiURL}/root-folder-id?id=${id}&type=${type}`,
				AuthHeader
			)
			.then((_data: any) => {
				if (_data?.data?.folder_id) {
					resolve(_data.data.folder_id);
				} else {
					resolve(null);
				}
			})
			.catch((e: any) => {
				reject(e);
			});
	});
};

const getFileMetadata = (tenantKey: any, key_id: any) => {
	return new Promise((resolve, reject) => {
		axios
			.get(
				`${url}/${tenantKey}/${apiURL}/get-metadata?key_id=${key_id}`,
				AuthHeader
			)
			.then((_data: any) => {
				if (_data?.data) {
					resolve(_data.data);
				} else {
					resolve(null);
				}
			})
			.catch((e: any) => {
				reject(e);
			});
	});
};

const listFolderItems = (
	tenant_key: any,
	key_id: any,
	folder_id: any,
	container_id: any,
	folder_name: string,
	type: string
) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenant_key}/${apiURL}/list-items`,
				{
					tenant_key,
					key_id,
					folder_id,
					container_id,
					folder_name,
					type,
				},
				AuthHeader
			)
			.then((_data: any) => {
				if (_data?.data) {
					resolve(_data.data);
				} else {
					resolve(null);
				}
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

const createNewFolder = (
	foldername: any,
	tenantKey: any,
	id: any,
	folder_id: any,
	container_id: any,
	folder_hierarchy: any,
	user: any,
	type: string,
	other_params: any
) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenantKey}/${apiURL}/create-folder`,
				{
					foldername,
					tenant_key: tenantKey,
					id,
					folder_id,
					container_id,
					folder_hierarchy,
					user,
					type,
					other_params,
				},
				AuthHeader
			)
			.then((_data: any) => {
				if (_data?.data && _data.data.length) {
					resolve(_data.data);
				} else {
					resolve(null);
				}
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

const uploadFiles = (
	files: any,
	tenantKey: string,
	type: string,
	key_id: string,
	folder_name: string,
	folder_id: string,
	container_id: string,
	folder_hierarchy: any,
	user: any,
	socket_id: any,
	other_params: any
) => {
	return new Promise((resolve, reject) => {
		const formData = new FormData();

		// Append each file to the formData object
		files.forEach((file: any, index: any) => {
			formData.append(`files`, file);
		});

		// Append other parameters
		formData.append("tenant_key", tenantKey);
		formData.append("key_id", key_id);
		formData.append("name", folder_name);
		formData.append("type", type);
		folder_id && formData.append("folder_id", folder_id);
		container_id && formData.append("container_id", container_id);
		formData.append("folder_hierarchy", folder_hierarchy);
		formData.append("email", user.email);
		formData.append("id", user.id);
		formData.append("socket_id", socket_id);
		formData.append("other_params", JSON.stringify(other_params));

		axios
			.post(`${url}/${tenantKey}/${apiURL}/upload`, formData, {
				...AuthHeader,
				onUploadProgress: (progressEvent: AxiosProgressEvent) => {
					if (progressEvent.lengthComputable) {
						Emitter.emit(
							"ACTION_PROGRESS",
							(((progressEvent?.progress || 0) / 2) * 100).toFixed(0)
						);
					}
				},
			})
			.then((_data: any) => {
				console.log("RESPONSE FROM UPLOAD FILES", _data);
				resolve(_data?.data || null);
			})
			.catch((e: any) => {
				reject(e);
			});
	});
};

const getFilePreview = (tenantKey: any, file_id: any, container_id: any) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenantKey}/${apiURL}/preview`,
				{
					file_id,
					container_id,
				},
				AuthHeader
			)
			.then((response: any) => {
				console.log(response.data);
				resolve(response.data);
			});
	});
};

const downloadFile = (fileid: any) => {};

const getFileThumbnail = (tenantKey: any, file_id: any, container_id: any) => {
	return new Promise((resolve, reject) => {
		axios
			.get(
				`${url}/${tenantKey}/${apiURL}/thumbnail?file_id=${file_id}&container_id=${container_id}`,
				AuthHeader
			)
			.then((response: any) => {
				console.log(response.data);
				resolve(response.data);
			});
	});
};

const getFile = (tenantKey: any, file_id: any, container_id: any) => {
	return new Promise((resolve, reject) => {
		axios
			.get(
				`${url}/${tenantKey}/${apiURL}/get-file?file_id=${file_id}&container_id=${container_id}`,
				AuthHeader
			)
			.then((response: any) => {
				console.log(response.data);
				resolve(response.data);
			});
	});
};

/** Where an item sits below a documents root, resolved by id. */
const getFolderPath = (
	tenantKey: string,
	itemId: string,
	rootId: string
): Promise<SharepointFolderPath> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${url}/${tenantKey}/${apiURL}/folder-path`, {
				...AuthHeader,
				params: { item_id: itemId, root_id: rootId },
			})
			.then((response: any) => {
				const { folders, item } = response.data ?? {};
				if (Array.isArray(folders) && item?.id) {
					resolve({ folders, item });
				} else {
					reject(new Error("Unexpected folder path response"));
				}
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

/**
 * A document attached to a report item, such as a finding, as bytes. It comes
 * through the download route, which also serves one staged but not yet saved.
 * A URL stored on a document is never followed: report data comes from users.
 */
const fetchReportDocument = (
	tenantKey: string,
	folderId: string | undefined,
	file: any,
	signal?: AbortSignal
): Promise<ArrayBuffer> =>
	axios
		.post(
			`${url}/${encodeURIComponent(tenantKey)}/${apiURL}/download?folder_id=${encodeURIComponent(folderId ?? "")}`,
			{
				file_name: file.file_name,
				original_name: file.actual_name || file.name, // NOSONAR: an empty stored name must fall back too
				file_type: file.type || file.file_type || "", // NOSONAR: an empty stored type must fall back too
				upload_path: "uploads",
				sharepoint_id: file.sharepoint_id,
			},
			{ ...AuthHeader, responseType: "arraybuffer", signal }
		)
		.then((response) => response.data);

/** A link to SharePoint's preview of a file stored there. */
const getReportDocumentPreviewUrl = (
	tenantKey: string,
	itemId: string,
	signal?: AbortSignal
): Promise<string> =>
	axios
		.get(`${url}/${encodeURIComponent(tenantKey)}/${apiURL}/item-preview`, {
			...AuthHeader,
			params: { item_id: itemId },
			signal,
		})
		.then((response) => {
			const link = response.data?.url;
			if (typeof link !== "string") {
				throw new TypeError("Unexpected preview response");
			}
			return link;
		});

/**
 * Search sharepoint collection by name
 * @param tenantKey - The tenant key
 * @param query - Search query string
 * @param limit - Maximum number of results (default: 20)
 */
const searchSharepoint = (
	tenantKey: string,
	query: string,
	keyId: string,
	limit: number = 20
) => {
	return new Promise((resolve, reject) => {
		axios
			.get(
				`${url}/${tenantKey}/${apiURL}/search?query=${encodeURIComponent(
					query
				)}&key_id=${encodeURIComponent(keyId)}&limit=${limit}`,
				AuthHeader
			)
			.then((response: any) => {
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

const collabo = (
	tenantKey: any,
	file_id: any,
	container_id: any,
	user: any
) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenantKey}/${apiURL}/collaboration`,
				{
					file_id,
					container_id,
					user,
				},
				AuthHeader
			)
			.then((response: any) => {
				console.log(response.data);
				resolve(response.data);
			});
	});
};

const deleteFiles = (
	tenantKey: any,
	item_ids: any,
	container_id: any,
	socket_id: any,
	props?: any
) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenantKey}/${apiURL}/remove`,
				{
					item_ids,
					container_id,
					socket_id,
					props,
				},
				AuthHeader
			)
			.then((response: any) => {
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

const renameFile = (
	tenantKey: any,
	item_id: any,
	container_id: any,
	new_name: any,
	user: any,
	props?: any
) => {
	return new Promise((resolve, reject) => {
		axios
			.patch(
				`${url}/${tenantKey}/${apiURL}/rename`,
				{
					item_id,
					container_id,
					new_name,
					user,
					props,
				},
				AuthHeader
			)
			.then((response: any) => {
				console.log(response);
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

export const moveFile = (
	tenantKey: any,
	container_id: any,
	item_ids: any,
	move_to_id: any,
	user: any,
	props?: any
) => {
	return new Promise((resolve, reject) => {
		axios
			.patch(
				`${url}/${tenantKey}/${apiURL}/move`,
				{
					item_ids,
					container_id,
					move_to_id,
					user,
					props,
				},
				AuthHeader
			)
			.then((response: any) => {
				console.log(response);
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

/**
 * Upload a report's pending attachments and remove dropped ones. Resolves the
 * server body for 200 and 207 alike; the caller inspects `failed_uploads`.
 */
const postSaveReportFiles = (tenantKey: string, payload: any) => {
	return new Promise((resolve, reject) => {
		axios
			.post(`${url}/${tenantKey}/${apiURL}/post-save`, payload, AuthHeader)
			.then((response: any) => {
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

const syncNestedDocuments = (tenantKey: string, payload: any) => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				`${url}/${tenantKey}/sharepoint/nested-documents`,
				payload,
				AuthHeader
			)
			.then((response: any) => {
				resolve(response.data);
			})
			.catch((error: any) => {
				reject(error?.response?.data || error?.response || error);
			});
	});
};

export {
	getFileMetadata,
	getRootFolderId,
	listFolderItems,
	createNewFolder,
	uploadFiles,
	renameFile,
	deleteFiles,
	getFilePreview,
	downloadFile,
	getFileThumbnail,
	getFile,
	getFolderPath,
	fetchReportDocument,
	getReportDocumentPreviewUrl,
	collabo,
	searchSharepoint,
};

export { postSaveReportFiles, syncNestedDocuments };
